// src/pages/income/service/IncomeServiceDraftNew.jsx
// ✅ 수입데이터+수입기안 통합 작성 [4단계] — 용역사업. 다른 3개 사업과 달리
// _shared/IncomeDraftNew.jsx(종류 그리드 선택)를 쓰지 않는 독립 페이지다 — "종류"가
// 아니라 "거래처"(VENDOR_CONFIG, 20개 사업장)가 청구형(공단 계약 7곳, 내부기안+공문
// 페어)/제출형(13개 사업장, 단건) 분기를 결정하고, 제목·본문·붙임이 전부 거래처별로
// 자동 유도된다((구) 페이지 ServiceGiseongAutoFill.jsx/DraftDocumentNew.jsx의 로직을
// 그대로 이식) — 자유 입력 "종류 선택" 그리드 자체가 이 사업엔 없다.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import ApprovalLine from "../../../components/approval-line/ApprovalLine";
import ApprovalLineModal from "../../../components/approval-line/ApprovalLineModal";
import ApprovalLineConfirmDialog from "../../../components/approval-line/ApprovalLineConfirmDialog";
import { VENDOR_CONFIG } from "../../../components/document-type/ServiceGiseongAutoFill";
import YongyeokGisungModal from "./YongyeokGisungModal";
import { useTabActivated } from "../../../tabs/TabActivation";
import { draftNumberChangedLine, draftNumberChangedLines, formatDraftSaveError } from "../../../utils/draftNumberNotice";

const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];
const PAY_METHOD_TO_CODE = { 계좌이체: "transfer", 카드결제: "card", 현금결제: "cash" };
const NAMESPACE = "income-service-draft";
const PREVIEW_STORAGE_KEY = `${NAMESPACE}PreviewData`;
const PREVIEW_CHANNEL_NAME = `${NAMESPACE}-preview`;
const LIST_REFRESH_CHANNEL_NAME = `${NAMESPACE}-list-refresh`;
const PREVIEW_WINDOW_NAME = `${NAMESPACE.replace(/-/g, "")}Preview`;
const PREVIEW_ROUTE_PATH = "/income/service/draft/preview";
const LIST_ROUTE_PATH = "/income/service/draft/list";

function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const HEADER_BTN_STYLE = { width: "auto", flexShrink: 0, padding: "8px 20px", fontSize: 14, whiteSpace: "nowrap", color: "#222", background: "#fff", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer" };
const SAVE_HEADER_BTN_STYLE = { ...HEADER_BTN_STYLE, color: "#fff", background: "#0b5fff", border: "none", fontWeight: 600 };
// ✅ 라벨 폭 통일(모든 좌측 폼 행 공통, 예외 없음) — 가장 긴 라벨 "기안번호 (내부기안)"
// 기준 실측: 8자 한글(기안번호+내부기안) + 공백 1 + 괄호 2, 14px/font-weight 600
// 기준 한글 1자≈14.5px·공백≈4.5px·괄호≈5.5px로 계산하면 텍스트 폭만 약 132px.
// 여기에 라벨 좌우 패딩(10px×2=20px)을 더하면 최소 152px 필요 — 이 프로젝트엔 실제
// 브라우저 렌더링을 측정할 도구가 없어(canvas/puppeteer 등 미설치) 여유를 넉넉히 둔
// 170px로 고정(모든 행 공통, 개별 오버라이드 금지).
const ROW_GRID_STYLE = { display: "grid", gridTemplateColumns: "170px 1fr", gap: "8px 12px", alignItems: "stretch", marginBottom: 5, minHeight: 36 };
const ROW_LABEL_STYLE = { fontWeight: 600, color: "#374151", fontSize: 14, border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", boxSizing: "border-box", height: 36, padding: "0 10px", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", whiteSpace: "nowrap" };
const ROW_INPUT_STYLE = { width: "100%", height: 36, border: "1px solid #d1d5db", borderRadius: 6, padding: "0 10px", fontSize: 14, boxSizing: "border-box" };
const ROW_INPUT_READONLY_STYLE = { ...ROW_INPUT_STYLE, backgroundColor: "#e5e7eb" };

// ✅ (구) commitYongyeokFields와 동일 — 회차 확정 시 제목 조립. 본문/비고(desc)는
// (구) IncomeServiceNew.jsx처럼 자유 입력 textarea라 여기서 만들지 않는다 — PDF
// 미리보기(IncomeProposalPreview.jsx)도 청구형/제출형 템플릿의 본문 문단은
// formData.content가 아니라 yongyeokRound/siteTitle 등으로 직접 계산해 렌더링하므로
// desc를 자유 텍스트로 둬도 기안서 본문 표시는 영향받지 않는다(확인 완료).
function buildClaimTitle(round, config, workMonth) {
  const siteTitle = (config?.contractName || "").replace(/^\d{4}년\s*/, "");
  const [workYear] = (workMonth || "").split("-");
  const year = workYear || String(new Date().getFullYear());
  return `${year}년 ${siteTitle} 제${round}회 기성금 청구`;
}

function buildSubmitTitle(config, workMonth) {
  const [year, month] = (workMonth || "").split("-");
  const monthNum = month ? parseInt(month, 10) : "";
  return `${year}년 ${config.serviceName} ${monthNum}월 기성계 제출`;
}

export default function IncomeServiceDraftNew() {
  const navigate = useNavigate();

  useEffect(() => { document.title = "수입기안 - 용역"; }, []);

  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [claimDate, setClaimDate] = useState(() => toISO(new Date()));
  // ✅ (구) IncomeServiceNew.jsx와 동일하게 작성일이 바뀌면 청구일도 따라가되,
  // 사용자가 청구일을 직접 건드린 뒤에는(touched) 더 이상 덮어쓰지 않는다 —
  // 구 페이지는 조건 없이 매번 덮어썼지만, 이번 요청에서 그 조건을 추가함.
  const [claimDateTouched, setClaimDateTouched] = useState(false);
  useEffect(() => {
    if (!claimDateTouched) setClaimDate(draftDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftDate]);
  const [subcategory, setSubcategory] = useState("용역"); // ✅ (구) IncomeServiceNew.jsx와 동일 — 용역/기타
  const [workMonth, setWorkMonth] = useState(() => toISO(new Date()).slice(0, 7));
  const [amount, setAmount] = useState("");
  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");

  // ✅ 시작 번호(수동 채번) — (구) IncomeServiceNew.jsx 이식. draftDate가 바뀔 때마다
  // 서버의 다음 빈 번호(autoSeq)를 다시 조회해 입력칸 기본값으로 채운다(구 페이지와
  // 동일 — 날짜를 바꾸면 수동 입력값도 그 날짜의 자동 번호로 리셋됨). 사용자가 이
  // 값을 자동 번호보다 크게 올리면 저장 시 결번 경고를 띄운다.
  const [autoSeq, setAutoSeq] = useState(null);
  const [seqInput, setSeqInput] = useState("");
  // ✅ keep-alive 탭이라 다시 열어도 마운트가 유지돼 자동 번호가 처음 조회값에 멈춰
  // 있었다(2026-10-02 사고: 그 사이 삭제·저장된 번호를 몰라 번호가 갈라짐). 탭 재방문
  // 때마다 다시 조회한다. 같은 날짜의 재조회에서는 사용자가 직접 바꿔 둔 시작 번호를
  // 덮지 않는다 — 자동 번호 그대로였을 때만 새 자동 번호로 따라간다.
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  const seqFetchRef = useRef({ date: null, auto: null });
  useEffect(() => {
    if (!draftDate) return;
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(`/income-service/next-seq/?date=${draftDate}`);
        if (!ignore) {
          const prev = seqFetchRef.current;
          const sameDate = prev.date === draftDate;
          seqFetchRef.current = { date: draftDate, auto: data.next_seq };
          setAutoSeq(data.next_seq);
          setSeqInput((cur) => (
            sameDate && cur !== "" && cur !== String(prev.auto) ? cur : String(data.next_seq)
          ));
        }
      } catch (err) {
        console.error("❌ 다음 채번 조회 실패:", err);
      }
    })();
    return () => { ignore = true; };
  }, [draftDate, seqRefreshTick]);

  // 거래처 — vendor_app.Vendor(용역사업은 income.Partner가 아니라 이 모델을 씀,
  // income_service/models.py 확인).
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [showVendorList, setShowVendorList] = useState(false);
  const [vendorHighlight, setVendorHighlight] = useState(-1);

  const config = VENDOR_CONFIG[vendorInput];
  const tableType = config?.tableType || null; // "yongyeok_claim" | "yongyeok_submit" | null

  // 청구형 전용 — 회차/계약금액 등
  const [yongyeok, setYongyeok] = useState({ round: null, contractAmount: 0, prevAmount: 0, prevCumulative: 0, recordId: null });
  // ✅ (구) IncomeServiceNew.jsx의 "📌 내가 선택한 자료" 카드용 — 자동매칭/모달
  // 선택의 근거가 된 원본 기성계 레코드 스냅샷(기성월·회차·거래처·용역명·금액).
  const [gisungSource, setGisungSource] = useState(null);
  // ✅ 제출형 전용 — 기성계 모달에서 제출형 레코드를 골랐을 때만 채워짐. 저장 시
  // draft_document.gisung_record로 함께 보내 그 레코드를 "작성됨"으로 표시되게
  // 한다(청구형은 yongyeok.recordId가 동일 역할). 모달을 거치지 않고 그냥 거래처만
  // 골라 제출하는 기존 흐름에서는 null 그대로 — 결번/저장 로직 회귀 없음.
  const [submitGisungRecordId, setSubmitGisungRecordId] = useState(null);
  const [showGisungModal, setShowGisungModal] = useState(false);
  const [activeView, setActiveView] = useState("internal"); // "internal" | "official"

  // 제목/본문 — 거래처(+청구형은 회차) 확정 시 자동 유도, 자유 입력 아님((구) 페이지와 동일).
  const [titleDesc, setTitleDesc] = useState({ title: "", desc: "" });
  const [attachments, setAttachments] = useState([]);

  const [approversInternal, setApproversInternal] = useState([]);
  const [approversOfficial, setApproversOfficial] = useState([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalModalTarget, setApprovalModalTarget] = useState("internal");
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);

  const [saving, setSaving] = useState(false);

  const previewWindowRef = useRef(null);
  const previewChannelRef = useRef(null);
  const [previewBlocked, setPreviewBlocked] = useState(false);
  useEffect(() => {
    if (!("BroadcastChannel" in window)) return;
    previewChannelRef.current = new BroadcastChannel(PREVIEW_CHANNEL_NAME);
    return () => previewChannelRef.current?.close();
  }, []);
  useEffect(() => {
    return () => {
      try { new BroadcastChannel(PREVIEW_CHANNEL_NAME).postMessage({ type: "clear" }); } catch {}
    };
  }, []);

  // ✅ 거래처 목록 — vendors/ 전역 API(지출 쪽과 동일 소스), focus 복귀 시 재조회.
  async function loadVendors() {
    try {
      const { data } = await api.get("vendors/");
      setVendors(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 거래처 목록 불러오기 실패:", err);
    }
  }
  useEffect(() => { loadVendors(); }, []);
  useEffect(() => {
    const handleFocus = () => loadVendors();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);
  const filteredVendors = vendors.filter((v) => v.name.toLowerCase().includes(vendorInput.toLowerCase()) && VENDOR_CONFIG[v.name]);
  // ✅ 이름은 매칭되지만 20개 사업장(VENDOR_CONFIG)에 없어 걸러진 거래처 존재 여부
  // — "왜 안 뜨는지" 안내 없이 조용히 사라지는 문제 방지(사용자 실측 버그 리포트 계기)
  const nameMatchedButUnconfigured = vendorInput.trim() && vendors.some(
    (v) => v.name.toLowerCase().includes(vendorInput.toLowerCase()) && !VENDOR_CONFIG[v.name]
  );

  // ✅ 거래처 확정 시 초기화 + 제출형은 즉시 제목/본문/붙임 확정(회차 개념 없음),
  // 청구형은 아래 자동매칭 이펙트(또는 모달 선택)가 회차 확정 후 세팅.
  function pickVendor(name) {
    setVendorInput(name);
    setShowVendorList(false);
    setYongyeok({ round: null, contractAmount: 0, prevAmount: 0, prevCumulative: 0, recordId: null });
    setGisungSource(null);
    setSubmitGisungRecordId(null);
    setAmount("");
    setActiveView("internal");
    const cfg = VENDOR_CONFIG[name];
    if (cfg?.tableType === "yongyeok_submit") {
      setAttachments((cfg.attachments || []).map((a, i) => ({ id: i + 1, name: a.name, count: a.count })));
      setTitleDesc({ title: buildSubmitTitle(cfg, workMonth), desc: "" });
    } else if (cfg?.tableType === "yongyeok_claim") {
      setAttachments((cfg.attachments || []).map((a, i) => ({ id: i + 1, name: a.name, count: a.count })));
      setTitleDesc({ title: "", desc: "" }); // 회차 확정 전엔 비어있는 게 정상((구) 페이지와 동일)
    } else {
      setAttachments([]);
      setTitleDesc({ title: "", desc: "" });
    }
  }

  // ✅ 제출형 — workMonth 바뀌면 제목만 재계산(월만 바뀌어도 즉시 유도 가능).
  // desc는 사용자가 직접 입력하는 자유 텍스트라 여기서 건드리지 않는다.
  useEffect(() => {
    if (tableType !== "yongyeok_submit" || !config) return;
    setTitleDesc((prev) => ({ ...prev, title: buildSubmitTitle(config, workMonth) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableType, workMonth]);

  // ✅ 청구형 — 금액+귀속월이 기존 기성계 레코드와 정확히 일치하면 그 레코드의 회차를
  // 그대로 채택(자동매칭, (구) 페이지와 동일 로직). 못 찾으면 사용자가 아래 "기성계
  // 불러오기" 모달로 수동 확정해야 한다.
  useEffect(() => {
    if (tableType !== "yongyeok_claim" || !config?.contractId || !amount || !workMonth) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("gisung-gye/records/", { params: { contract: config.contractId } });
        const records = Array.isArray(data) ? data : data.results || [];
        if (cancelled) return;
        const claimAmount = Number(amount) || 0;
        const [wYear, wMonth] = workMonth.split("-");
        const monthNum = wMonth ? parseInt(wMonth, 10) : null;
        const matched = records.find((r) => Number(r.current_amount) === claimAmount && String(r.gisung_year) === String(wYear) && Number(r.gisung_month) === monthNum);
        if (!matched) return;
        const prevRecord = records.find((r) => r.gisung_round === matched.gisung_round - 1) || null;
        const contractAmount = Number(matched.contract_amount) || 0;
        const prevAmount = prevRecord ? Number(prevRecord.current_amount) || 0 : 0;
        const prevCumulative = contractAmount - (prevRecord ? Number(prevRecord.remaining_amount) || 0 : contractAmount);
        setYongyeok({ round: matched.gisung_round, contractAmount, prevAmount, prevCumulative, recordId: matched.id });
        setGisungSource({
          gisung_year: matched.gisung_year, gisung_month: matched.gisung_month, gisung_round: matched.gisung_round,
          institution: matched.institution, service_name: matched.service_name, current_amount: matched.current_amount,
        });
        setTitleDesc((prev) => ({ ...prev, title: buildClaimTitle(matched.gisung_round, config, workMonth) }));
      } catch (err) {
        console.error("❌ 기성계 자동 조회 실패:", err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableType, config?.contractId, amount, workMonth]);

  // ✅ 기성계 불러오기 전역화(20개 계약 전체) — 모달이 특정 거래처에 좁혀지지
  // 않으므로, 고른 레코드의 vendorName이 현재 선택된 거래처와 다를 수 있다(반대
  // 유형이 선택돼 있던 경우도 포함). 다르면 "거래처가 OO로 변경됩니다" confirm
  // 후 pickVendor로 전환하고, 그 위에 유형별 필드를 덮어쓴다(pickVendor의 초기화
  // setState와 이 함수의 setState가 같은 이벤트 핸들러 안에서 배치 처리되므로,
  // 아래 호출들이 항상 pickVendor의 리셋값을 덮어쓰는 마지막 값으로 반영된다).
  //
  // 청구형(tableType==="yongyeok_claim")은 기존 그대로 회차/계약금액 등을 계산해
  // yongyeok/gisungSource/제목에 반영(내부기안+공문 페어 로직 회귀 없음).
  // 제출형은 회차 개념이 없어 pickVendor가 세팅하는 제출형 제목/붙임을 그대로
  // 두고, submitGisungRecordId만 기록해 저장 시 그 기성계 레코드를 "작성됨"으로
  // 연결한다(문서 생성 자체는 기존 제출형 단건 로직 그대로 — doSubmit의 else 분기,
  // 공문 없음).
  function handleGisungPick(fields) {
    const { source, vendorName, tableType: pickedType, recordId } = fields;
    const cfg = vendorName ? VENDOR_CONFIG[vendorName] : config;

    const apply = () => {
      if (vendorName && vendorName !== vendorInput) {
        pickVendor(vendorName);
      }
      if (pickedType === "yongyeok_claim") {
        setYongyeok({ round: fields.round, contractAmount: fields.contractAmount, prevAmount: fields.prevAmount, prevCumulative: fields.prevCumulative, recordId: fields.recordId });
        setGisungSource(source || null);
        setTitleDesc((prev) => ({ ...prev, title: buildClaimTitle(fields.round, cfg, workMonth) }));
      } else {
        setSubmitGisungRecordId(recordId ?? null);
      }
      setShowGisungModal(false);
    };

    if (vendorName && vendorInput && vendorName !== vendorInput) {
      const ok = window.confirm(`거래처가 "${vendorName}"로 변경됩니다. 계속하시겠습니까?`);
      if (!ok) return;
    }
    apply();
  }

  // ── 미리보기 ──────────────────────────────────────────────
  const previewFormData = useMemo(() => {
    if (!config) return null;
    const base = {
      recipient: "내부결재",
      title: titleDesc.title,
      documentType: "income_draft",
      businessType: "용역사업",
      detailType: subcategory,
      detailOption: subcategory,
      workMonth,
      startDate: workMonth,
      endDate: workMonth,
      client: vendorInput,
      amount: amount ? Number(amount) : 0,
      transactionType: { 계좌이체: payMethodLabel === "계좌이체", 카드결제: payMethodLabel === "카드결제", 현금결제: payMethodLabel === "현금결제" },
      manualDate: claimDate,
      content: titleDesc.desc,
      attachments: attachments.filter((a) => a.name.trim() !== ""),
      tableType,
      activeView,
    };
    if (tableType === "yongyeok_claim") {
      return {
        ...base,
        yongyeokRound: yongyeok.round,
        yongyeokContractAmount: yongyeok.contractAmount,
        yongyeokPrevAmount: yongyeok.prevAmount,
        yongyeokPrevCumulative: yongyeok.prevCumulative,
        yongyeokContractName: config.contractName,
        yongyeokReferenceLabel: config.referenceLabel,
      };
    }
    if (tableType === "yongyeok_submit") {
      return {
        ...base,
        yongyeokSubmitServiceName: config.serviceName,
        yongyeokSubmitRecipient: config.recipient || "",
        yongyeokSubmitHonorific: config.honorific || "",
        yongyeokSubmitViaLabel: config.viaLabel || "",
      };
    }
    return base;
  }, [config, titleDesc, workMonth, vendorInput, amount, payMethodLabel, claimDate, attachments, tableType, activeView, yongyeok, subcategory]);

  function syncPreviewData(formData, approvalLine) {
    if (!formData) return;
    const payload = { formData, approvalLine };
    sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(payload));
    previewChannelRef.current?.postMessage({ type: "update", payload });
  }

  useEffect(() => {
    const approvalLine = activeView === "official" ? approversOfficial : approversInternal;
    const timer = setTimeout(() => syncPreviewData(previewFormData, approvalLine), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewFormData, approversInternal, approversOfficial, activeView]);

  function openPreviewWindow(action = "preview") {
    if (!previewFormData) return;
    syncPreviewData(previewFormData, activeView === "official" ? approversOfficial : approversInternal);
    const path = `${PREVIEW_ROUTE_PATH}${action !== "preview" ? `?action=${action}` : ""}`;
    if (previewWindowRef.current && !previewWindowRef.current.closed) {
      previewWindowRef.current.focus();
      if (action === "download" || action === "print") previewWindowRef.current.location.search = `?action=${action}`;
      setPreviewBlocked(false);
      return;
    }
    const win = window.open(path, PREVIEW_WINDOW_NAME, "width=900,height=1200");
    if (!win) { setPreviewBlocked(true); return; }
    previewWindowRef.current = win;
    setPreviewBlocked(false);
  }

  // ── 붙임 ──────────────────────────────────────────────────
  const addAttachment = () => setAttachments((prev) => [...prev, { id: Date.now(), name: "", count: 1 }]);
  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));
  const setAttachmentField = (id, key, value) => setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, [key]: value } : a)));

  // ── 저장 ──────────────────────────────────────────────────
  async function handleSubmit() {
    if (!vendorInput.trim() || !config) return alert("거래처를 선택해주세요.");
    if (!amount || Number(amount) <= 0) return alert("금액을 입력해주세요.");
    if (tableType === "yongyeok_claim" && !yongyeok.round) return alert('회차가 확정되지 않았습니다. "기성계 불러오기"로 회차를 확정해주세요.');
    if (tableType === "yongyeok_claim") {
      if (approversInternal.length === 0 || approversOfficial.length === 0) return alert("내부기안·공문 결재선을 모두 등록해주세요.");
    } else if (approversInternal.length === 0) {
      return alert("결재선을 등록해주세요.");
    }
    // ✅ (구) IncomeServiceNew.jsx와 동일한 결번 경고 — 시작 번호를 자동 채번보다
    // 크게 잡으면 그 사이 번호들이 결번으로 남는다는 것을 저장 전에 알린다.
    const currentSeq = parseInt(seqInput, 10) || autoSeq || 1;
    if (autoSeq != null && currentSeq > autoSeq) {
      const ok = window.confirm(
        `⚠️ ${String(autoSeq).padStart(3, "0")}호~${String(currentSeq - 1).padStart(3, "0")}호가 결번으로 남습니다.\n계속 진행하시겠습니까?`
      );
      if (!ok) return;
    }
    setShowApprovalConfirm(true);
  }

  const toApprovalLinesData = (approvers) => approvers.map((a, idx) => ({ approver_id: a.id, approval_type: a.status || "결재", order: idx + 1, position: a.position || "" }));

  async function doSubmit() {
    setShowApprovalConfirm(false);
    setSaving(true);
    try {
      const attachment_items = attachments.filter((a) => a.name.trim()).map((a) => ({ name: a.name, count: a.count }));

      // ✅ 시작 번호를 자동 채번값과 다르게(직접 수정) 잡았을 때만 start_seq를
      // 보낸다 — 그대로 두면 기존과 동일하게 서버가 매 저장 시점에 새로 계산한
      // "가장 빠른 빈 번호"를 쓴다(회귀 금지: 안 건드리면 이전과 완전히 동일).
      const currentSeq = parseInt(seqInput, 10) || autoSeq || 1;
      const startSeqOverride = (autoSeq != null && currentSeq !== autoSeq) ? currentSeq : null;
      const yymmdd = draftDate.replaceAll("-", "").slice(2);

      if (tableType === "yongyeok_claim") {
        // 화면에 보여준 번호(draftNoInternalPreview/OfficialPreview와 같은 계산)
        const requestedNo = (n) => `금천일자리(주)용역-${yymmdd}-${String(n).padStart(3, "0")}호`;
        const payload = {
          draft_date: draftDate, claim_date: claimDate || null, work_month: workMonth,
          partner_id: null, partner_name: vendorInput, amount: Number(amount) || 0,
          pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer", subcategory,
          gisung_record: yongyeok.recordId,
          ...(startSeqOverride != null ? { start_seq: startSeqOverride } : {}),
          internal: { title: titleDesc.title, desc: titleDesc.desc, attachment_items, approval_lines_data: toApprovalLinesData(approversInternal), requested_draft_no: requestedNo(currentSeq) },
          official: { title: titleDesc.title, desc: titleDesc.desc, attachment_items, approval_lines_data: toApprovalLinesData(approversOfficial), requested_draft_no: requestedNo(currentSeq + 1) },
        };
        const { data } = await api.post("/income-service/draft-pair/", payload);
        await Promise.all(data.draft_documents.map((d) => api.post(`/draft/documents/${d.id}/submit/`)));
        previewChannelRef.current?.postMessage({ type: "clear" });
        try { new BroadcastChannel(LIST_REFRESH_CHANNEL_NAME).postMessage({ type: "refresh" }); } catch {}
        const changed = draftNumberChangedLines(data.draft_numbers);
        alert(`✅ 저장 및 상신 완료!\n내부기안: ${data.draft_documents[0].draft_no}\n공문: ${data.draft_documents[1].draft_no}${changed ? `\n\n${changed}` : ""}`);
        setSeqInput(""); // 이 탭은 keep-alive라 남는다 — 다음 방문 때 새 자동 번호를 받게 비운다
        navigate(LIST_ROUTE_PATH);
      } else {
        const guessSeq = startSeqOverride != null ? startSeqOverride : (autoSeq || 1);
        const payload = {
          subcategory, draft_date: draftDate, claim_date: claimDate || null, work_month: workMonth,
          partner_name: vendorInput, amount: Number(amount) || 0, pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
          desc: titleDesc.desc, title: titleDesc.title,
          ...(startSeqOverride != null ? { start_seq: startSeqOverride } : {}),
          draft_document: {
            document_type: "income_draft", category: "용역사업", subcategory, detail_option: subcategory,
            title: titleDesc.title, draft_date: draftDate, work_month: workMonth, partner_name: vendorInput,
            amount: Number(amount) || 0, pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer", desc: titleDesc.desc,
            recipient: "내부결재", attachment_items, seq: String(guessSeq).padStart(3, "0"), draft_no: `금천일자리(주)용역-${yymmdd}-${String(guessSeq).padStart(3, "0")}호`,
            approval_lines_data: toApprovalLinesData(approversInternal),
            // ✅ 기성계 모달에서 제출형 레코드를 골라 저장하는 경우에만 채워짐 —
            // 그 레코드를 "작성됨"으로 연결해 모달의 미작성/작성 필터가 정확히
            // 반영되게 한다(청구형은 gisung_record가 이미 위 청구형 분기에서 처리됨).
            ...(submitGisungRecordId != null ? { gisung_record: submitGisungRecordId } : {}),
          },
        };
        const { data } = await api.post("/income-service/draft/", payload);
        await api.post(`/draft/documents/${data.draft_document.id}/submit/`);
        previewChannelRef.current?.postMessage({ type: "clear" });
        try { new BroadcastChannel(LIST_REFRESH_CHANNEL_NAME).postMessage({ type: "refresh" }); } catch {}
        const changed = draftNumberChangedLine(data.draft_number);
        alert(`✅ 저장 및 상신 완료!\n기안번호: ${data.draft_document.draft_no}${changed ? `\n\n${changed}` : ""}`);
        setSeqInput(""); // 이 탭은 keep-alive라 남는다 — 다음 방문 때 새 자동 번호를 받게 비운다
        navigate(LIST_ROUTE_PATH);
      }
    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);
      alert(formatDraftSaveError(err));
    } finally {
      setSaving(false);
    }
  }

  const isClaim = tableType === "yongyeok_claim";

  // ✅ 기안번호 미리보기 — (구) IncomeServiceNew.jsx의 draftNoInternal/draftNoOfficial
  // 재현. seqInput(시작 번호)이 바뀌면 매 렌더마다 같이 재계산되므로 입력과 동시에
  // 갱신된다(별도 useEffect 불필요).
  const previewYymmdd = draftDate ? draftDate.replaceAll("-", "").slice(2) : "";
  const previewSeqNum = parseInt(seqInput, 10) || autoSeq || 1;
  const draftNoInternalPreview = `금천일자리(주)용역-${previewYymmdd}-${String(previewSeqNum).padStart(3, "0")}호`;
  const draftNoOfficialPreview = `금천일자리(주)용역-${previewYymmdd}-${String(previewSeqNum + 1).padStart(3, "0")}호`;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ margin: 0, whiteSpace: "nowrap" }}>용역사업 수입데이터+기안 작성</h2>
          {/* ✅ (구) IncomeServiceNew.jsx와 동일 — 초록 배경, 제목 오른쪽, 페이지 진입
              시부터 항상 활성 상태로 표시. 모달 자체가 전역 조회(청구형 7개 계약
              전체)로 바뀌어 특정 거래처 선택을 기다릴 필요가 없어짐 — 거래처
              선택 여부와 무관하게 항상 클릭 가능. */}
          <button
            type="button"
            onClick={() => setShowGisungModal(true)}
            style={{ ...HEADER_BTN_STYLE, color: "#fff", background: "#2E7D32", border: "none", fontWeight: 600 }}
          >
            📋 기성계 불러오기{yongyeok.round ? ` (현재: 제${yongyeok.round}회)` : ""}
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={() => openPreviewWindow("print")} style={HEADER_BTN_STYLE}>🖨 기안 인쇄</button>
          <button onClick={() => openPreviewWindow("preview")} style={HEADER_BTN_STYLE}>👁️ PDF 미리보기</button>
          <button onClick={() => openPreviewWindow("download")} style={HEADER_BTN_STYLE}>📥 PDF 다운로드</button>
          <button onClick={handleSubmit} disabled={saving} style={{ ...SAVE_HEADER_BTN_STYLE, marginLeft: 12, background: saving ? "#999" : SAVE_HEADER_BTN_STYLE.background }}>
            {saving ? "저장 중…" : "저장 및 상신"}
          </button>
        </div>
      </div>

      {previewBlocked && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, padding: "8px 12px", background: "#fff3cd", border: "1px solid #ffe08a", borderRadius: 6, color: "#664d03", fontSize: 13 }}>
          <span>브라우저가 미리보기 팝업 창을 차단했습니다.</span>
          <button onClick={() => openPreviewWindow("preview")} style={{ ...HEADER_BTN_STYLE, background: "#664d03", color: "#fff", border: "none" }}>미리보기 창 열기</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 16 }}>
        {/* ✅ (구) IncomeServiceNew.jsx의 필드 순서를 기준으로 재배치(입력 방식은 신버전
            유지): 시작번호→기안번호(내부)→기안번호(공문)→제목(자동)→세부구분→
            작성일/청구일→귀속월→거래처→금액→거래방법→본문/비고.
            시작 번호 행의 안내문("자동 채번 (빈 번호 있으면 직접 수정 가능)")이 라벨+
            80px 입력칸과 한 줄에 들어가야 해서 좌측 컬럼 폭을 420px→560px로 넓힘. */}
        <div style={{ flex: "0 0 560px", display: "flex", flexDirection: "column" }}>
          {/* ① 시작 번호 — 라벨/입력칸(80px)/안내문구 한 줄 */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>시작 번호</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="number"
                min={1}
                value={seqInput}
                onChange={(e) => setSeqInput(e.target.value)}
                style={{ ...ROW_INPUT_STYLE, width: 80, flexShrink: 0 }}
              />
              <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
                자동 채번 (빈 번호 있으면 직접 수정 가능)
              </span>
            </div>
          </div>

          {/* ② 기안번호(내부기안) / ③ 기안번호(공문) — 자동생성 필드(회색 읽기전용).
              공문은 청구형(내부기안+공문 페어)에서만 의미가 있어 제출형엔 숨김. */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>기안번호 (내부기안)</div>
            <input value={draftNoInternalPreview} readOnly style={ROW_INPUT_READONLY_STYLE} />
          </div>
          {isClaim && (
            <div style={ROW_GRID_STYLE}>
              <div style={ROW_LABEL_STYLE}>기안번호 (공문)</div>
              <input value={draftNoOfficialPreview} readOnly style={ROW_INPUT_READONLY_STYLE} />
            </div>
          )}

          {/* ④ 제목(자동) */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>제목(자동)</div>
            <input value={titleDesc.title} readOnly style={ROW_INPUT_READONLY_STYLE} placeholder="거래처를 선택하면 자동 생성됩니다." />
          </div>

          {/* ⑤ 세부구분 */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>세부구분</div>
            <select value={subcategory} onChange={(e) => setSubcategory(e.target.value)} style={ROW_INPUT_STYLE}>
              <option value="용역">용역</option>
              <option value="기타">기타</option>
            </select>
          </div>

          {/* ⑥ 작성일/청구일 — 신버전 date input 유지. 좌우 절반(82px 미니 라벨)로
              배치하면 라벨 폭이 다른 행(170px)과 달라져 "모든 행 라벨 폭 통일"
              원칙이 깨지므로, 각자 완전한 ROW_GRID_STYLE 행(다른 필드와 동일한
              170px 라벨)으로 세로 배치 — 정렬 일관성 우선(구버전처럼 두 줄). */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>작성일</div>
            <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} style={ROW_INPUT_STYLE} />
          </div>
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>청구일</div>
            <input type="date" value={claimDate} onChange={(e) => { setClaimDate(e.target.value); setClaimDateTouched(true); }} style={ROW_INPUT_STYLE} />
          </div>

          {/* ⑦ 귀속월 — 신버전 month input 유지 */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>귀속월</div>
            <input type="month" value={workMonth} onChange={(e) => setWorkMonth(e.target.value)} style={ROW_INPUT_STYLE} />
          </div>

          {/* ⑧ 거래처 (+ 청구형/제출형 안내, 회차 미확정 경고, 선택자료 카드는
              거래처 선택에 직접 딸린 맥락 정보라 바로 아래에 유지) */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>거래처</div>
            <div style={{ minWidth: 0, position: "relative" }}>
              <input
                value={vendorInput}
                onChange={(e) => { setVendorInput(e.target.value); setShowVendorList(true); setVendorHighlight(-1); }}
                onFocus={() => vendorInput.trim() && setShowVendorList(true)}
                onKeyDown={(e) => {
                  if (!showVendorList || !filteredVendors.length) return;
                  if (e.key === "ArrowDown") { e.preventDefault(); setVendorHighlight((i) => (i < filteredVendors.length - 1 ? i + 1 : 0)); }
                  else if (e.key === "ArrowUp") { e.preventDefault(); setVendorHighlight((i) => (i > 0 ? i - 1 : filteredVendors.length - 1)); }
                  else if (e.key === "Enter" && vendorHighlight >= 0) { e.preventDefault(); pickVendor(filteredVendors[vendorHighlight].name); }
                  else if (e.key === "Escape") setShowVendorList(false);
                }}
                placeholder="20개 사업장 거래처명 중 입력(청구형 7곳/제출형 13곳)"
                style={ROW_INPUT_STYLE}
                autoComplete="off"
              />
              {showVendorList && filteredVendors.length > 0 && (
                <ul style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #d1d5db", borderRadius: 4, maxHeight: 240, overflowY: "auto", zIndex: 20, boxShadow: "0 4px 6px rgba(0,0,0,0.15)", marginTop: 2, padding: 0, listStyle: "none" }}>
                  {filteredVendors.map((v, i) => (
                    <li key={v.id} onMouseDown={() => pickVendor(v.name)} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, background: i === vendorHighlight ? "#f0f9ff" : "#fff" }}>{v.name}</li>
                  ))}
                </ul>
              )}
              {showVendorList && filteredVendors.length === 0 && nameMatchedButUnconfigured && (
                <div style={{ fontSize: 12, color: "#8a6d1a", background: "#fff3cd", border: "1px solid #ffe08a", borderRadius: 6, padding: "6px 10px", marginTop: 4 }}>
                  ⚠️ 거래처 관리에는 등록돼 있지만, 이 페이지가 자동채움하는 20개 계약 사업장 목록에는 없는 이름입니다. 신규 계약 사업장이면 담당자에게 계약 정보(청구형/제출형, 발주처, 붙임서류)를 확인해 등록을 요청하세요.
                </div>
              )}
            </div>
          </div>

          {config && (
            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
              {isClaim ? "청구형(공단 계약) — 내부기안+공문 2건 생성" : "제출형(기성계 제출) — 단건 생성"}
            </div>
          )}

          {isClaim && (
            <>
              {!yongyeok.round && (
                <div style={{ fontSize: 12, color: "#8a6d1a", background: "#fff3cd", border: "1px solid #ffe08a", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
                  ⚠️ 회차 미확정 — 금액+귀속월이 기존 레코드와 일치하면 자동 확정되고, 없으면 위 버튼으로 직접 확정하세요.
                </div>
              )}
              {gisungSource && (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#166534", marginBottom: 8 }}>
                  <div style={{ fontWeight: "bold", fontSize: 12, color: "#15803d", marginBottom: 8, borderBottom: "1px solid #86efac", paddingBottom: 6, whiteSpace: "nowrap" }}>
                    📌 내가 선택한 자료
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "4px 16px" }}>
                    <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>기성월</span>
                    <span>{gisungSource.gisung_year}.{String(gisungSource.gisung_month).padStart(2, "0")}</span>
                    <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>회차</span>
                    <span>{gisungSource.gisung_round}회차</span>
                    <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>거래처(발주처)</span>
                    <span>{gisungSource.institution || "-"}</span>
                    <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>용역명</span>
                    <span>{gisungSource.service_name || "-"}</span>
                    <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>금회기성금액</span>
                    <span style={{ fontWeight: "bold" }}>{Number(gisungSource.current_amount || 0).toLocaleString()}원</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ⑨ 금액 */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>금액</div>
            <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))} style={ROW_INPUT_STYLE} />
          </div>

          {/* ⑩ 거래방법 */}
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>거래방법</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", height: 36 }}>
              {PAY_METHOD_LABELS.map((label) => (
                <label key={label} style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                  <input type="radio" checked={payMethodLabel === label} onChange={() => setPayMethodLabel(label)} /> {label}
                </label>
              ))}
            </div>
          </div>

          {/* ⑪ 본문/비고 — (구) IncomeServiceNew.jsx와 동일한 자유 입력 textarea, 다른
              행과 같은 라벨-입력 가로 정렬(라벨은 상단 정렬, textarea 좌측 라인은
              다른 입력칸과 동일하게 ROW_INPUT_STYLE 기반). 자동 생성이 아니라 사용자가
              직접 청구 관련 메모를 적는 필드 — PDF 미리보기 본문 문단은 이 값을 쓰지
              않고 회차·거래처 등으로 직접 계산되므로(위 buildXXXTitle 주석 참고) 자유
              입력으로 둬도 기안서 본문 표시에는 영향 없음. */}
          <div style={{ ...ROW_GRID_STYLE, alignItems: "start" }}>
            <div style={{ ...ROW_LABEL_STYLE, height: "auto", alignSelf: "start" }}>본문/비고</div>
            <textarea
              rows={4}
              value={titleDesc.desc}
              onChange={(e) => setTitleDesc((prev) => ({ ...prev, desc: e.target.value }))}
              placeholder="청구 관련 메모를 입력하세요."
              style={{ ...ROW_INPUT_STYLE, height: "auto", padding: "8px 10px", resize: "vertical" }}
            />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 480 }}>
          {/* 붙임 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <strong>붙임</strong>
              <button type="button" onClick={addAttachment} style={{ height: 28, color: "#222" }}>+ 행 추가</button>
            </div>
            {attachments.map((a) => (
              <div key={a.id} style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
                <input value={a.name} onChange={(e) => setAttachmentField(a.id, "name", e.target.value)} style={{ flex: 1, height: 32 }} placeholder="붙임 이름" />
                <input type="number" value={a.count} onChange={(e) => setAttachmentField(a.id, "count", Number(e.target.value) || 1)} style={{ width: 50, height: 32 }} />
                <span>부</span>
                <button type="button" onClick={() => removeAttachment(a.id)} style={{ width: 28, height: 28, color: "#222" }}>✕</button>
              </div>
            ))}
          </div>

          {isClaim ? (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <button type="button" onClick={() => setActiveView("internal")} style={{ ...HEADER_BTN_STYLE, background: activeView === "internal" ? "#2563eb" : "#fff", color: activeView === "internal" ? "#fff" : "#222" }}>내부기안 미리보기</button>
                <button type="button" onClick={() => setActiveView("official")} style={{ ...HEADER_BTN_STYLE, background: activeView === "official" ? "#2563eb" : "#fff", color: activeView === "official" ? "#fff" : "#222" }}>공문 미리보기</button>
              </div>
              <strong>내부기안 결재선</strong>
              <ApprovalLine approvers={approversInternal} setApprovers={setApproversInternal} onOpenModal={() => { setApprovalModalTarget("internal"); setShowApprovalModal(true); }} documentType="income_draft" />
              <div style={{ marginTop: 12 }}>
                <strong>공문 결재선</strong>
                <ApprovalLine approvers={approversOfficial} setApprovers={setApproversOfficial} onOpenModal={() => { setApprovalModalTarget("official"); setShowApprovalModal(true); }} documentType="income_draft" />
              </div>
            </>
          ) : (
            <ApprovalLine approvers={approversInternal} setApprovers={setApproversInternal} onOpenModal={() => { setApprovalModalTarget("internal"); setShowApprovalModal(true); }} documentType="income_draft" />
          )}
        </div>
      </div>

      {showGisungModal && (
        <YongyeokGisungModal onClose={() => setShowGisungModal(false)} onPick={handleGisungPick} />
      )}

      {showApprovalModal && (
        <ApprovalLineModal
          onClose={() => setShowApprovalModal(false)}
          onSelect={(selected) => {
            if (approvalModalTarget === "official") setApproversOfficial(selected);
            else setApproversInternal(selected);
            setShowApprovalModal(false);
          }}
          documentType="income_draft"
          initialApprovers={approvalModalTarget === "official" ? approversOfficial : approversInternal}
        />
      )}
      {showApprovalConfirm && (
        <ApprovalLineConfirmDialog
          approvers={isClaim ? [...approversInternal, ...approversOfficial] : approversInternal}
          onCancel={() => setShowApprovalConfirm(false)}
          onConfirm={doSubmit}
        />
      )}
    </div>
  );
}
